// Ref-counted pause flags for the kiosk / PIN-session auto-logout timer.
//
// The auto-logout countdown (src/components/ui/logout-timer.tsx) freezes itself
// while an employee is actively working through the Checklists, Training or
// My Tickets module, so a long checklist, a training video, or typing up a bug
// report doesn't log them out mid-task on a shared kiosk.
//
// #1247 (Ivan Guizar / Fresh Fill Madera) added 'ticket_active': writing a
// ticket is exactly the same shape of work as a checklist item -- minutes of
// typing with no clicks the timer counts as activity -- and it logged him out
// mid-ticket. His words: "I wish it was like the Checklist where it stops it
// as long as you're on the page."
//
// #1394 (Yuvia Cabrera / Fresh Fill Visalia) added 'temp_log_active' for the
// same reason: a run of several equipment readings, each a quick tap with a
// pause to read the thermometer, was getting counted as idle time and logging
// her out after 2-3 checks.
//
// #1862 (Jonathon Ochesky / Fresh Fill Visalia) added 'survey_active': filling
// out a survey is reading and typing with long gaps between taps, same as a
// checklist or a ticket, and team members were getting booted mid-survey
// before they could finish in one sitting.
//
// Both a module's LIST view AND the dialog it opens (an individual checklist
// item, or the training player) legitimately want the timer paused. With a
// plain `localStorage['checklist_active'] = 'true'` boolean, closing the inner
// dialog cleared the flag while the outer list view was still active, resuming
// the timer prematurely. We therefore ref-count each pause: the timer stays
// paused until the LAST owner releases it.
//
// Values are stored back in the original keys as integer strings so the reset
// sites that hard-clear them on logout (`localStorage.removeItem(...)`) still
// work — a missing key reads as a count of zero.

export type TimerPauseKind =
  | 'checklist_active'
  | 'training_active'
  | 'ticket_active'
  | 'temp_log_active'
  | 'survey_active';

const PAUSE_KINDS: TimerPauseKind[] = [
  'checklist_active',
  'training_active',
  'ticket_active',
  'temp_log_active',
  'survey_active',
];

function readCount(kind: TimerPauseKind): number {
  try {
    const raw = localStorage.getItem(kind);
    if (raw === null) return 0;
    // Back-compat: an earlier build stored the literal 'true' for a single
    // owner. Treat it as a count of one so a mixed transition still pauses.
    if (raw === 'true') return 1;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeCount(kind: TimerPauseKind, count: number): void {
  try {
    if (count > 0) localStorage.setItem(kind, String(count));
    else localStorage.removeItem(kind);
  } catch {
    /* localStorage unavailable (SSR / privacy mode) — nothing to persist */
  }
}

/** Request a timer pause for this owner. Balance with releaseTimerPause. */
export function acquireTimerPause(kind: TimerPauseKind): void {
  writeCount(kind, readCount(kind) + 1);
}

/** Drop this owner's timer pause. The timer resumes only once all owners release. */
export function releaseTimerPause(kind: TimerPauseKind): void {
  writeCount(kind, Math.max(0, readCount(kind) - 1));
}

/** True while any owner is holding a checklist or training pause. */
export function isTimerPaused(): boolean {
  return PAUSE_KINDS.some((kind) => readCount(kind) > 0);
}

/** Hard-reset every pause. Used at logout / new PIN login so the next user starts clean. */
export function clearTimerPauses(): void {
  PAUSE_KINDS.forEach((kind) => writeCount(kind, 0));
}
